import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

export const EditDealerQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [quotation, setQuotation] = useState(null);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  console.log("quotation", quotation)

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.DEALER_QUOTATIONS.GET(id));
        setQuotation(response.data.quotation);
        setProducts(response.data.quotation.dealer_quotation_products || []);
        setError(null);
      } catch (error) {
        console.error("Error fetching quotation:", error);
        setError("Failed to load quotation. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuotation();
    }
  }, [id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Allow empty value or valid numbers
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setQuotation((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleProductCostChange = (productId, newCost) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId ? { ...product, cost: newCost } : product
      )
    );
  };

  const calculateProductCost = () => {
    return products.reduce((sum, product) => {
      const cost = parseFloat(product.cost) || 0;
      if (product.quantity !== null) {
        return sum + (parseFloat(product.quantity) || 0) * cost;
      } else if (product.pieces !== null) {
        return sum + cost; // Just add the cost when it's pieces
      }
      return sum;
    }, 0);



    
  };

  const calculateSubtotal = () => {
    const productTotal = calculateProductCost();
    const bendingCharges = quotation?.customer_order
      ? parseFloat(quotation?.bending_charges) || 0 
      : 0;
    const transportCharges = parseFloat(quotation?.transport_charges) || 0;

    return productTotal + bendingCharges + transportCharges;
  };

  const calculateGST = () => {
    return calculateSubtotal() * 0.18; // 18% GST
  };

  const calculateTotalAmount = () => {
    return calculateSubtotal() + calculateGST();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Update quotation details
      await axios.put(API_ENDPOINTS.DEALER_QUOTATIONS.EDIT(id), {
        bending_charges: quotation.bending_charges,
        transport_charges: quotation.transport_charges,
        gst_amount: calculateGST(), 
        total_amount: calculateTotalAmount(),
        customer_order_id: quotation.custom_order_id,
        status: "pending",
        products,

        app_user_id: quotation.app_user_id,
      });
      // Update product costs
      await Promise.all(
        products.map((product) =>
          axios.put(API_ENDPOINTS.DEALER_QUOTATION_PRODUCTS.EDIT(product.id), {
            cost: product.cost,
          })
        )
      );
      setSuccess("Quotation and products updated successfully!");
      setTimeout(() => navigate("/dealer-quotations"), 2000);
    } catch (error) {
      console.error("Error updating quotation or products:", error);
      setError("Failed to update quotation or products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !quotation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!quotation) {
    return (
      <Box p={3}>
        <Alert severity="info">No quotation data available.</Alert>
      </Box>
    );
  }

  // Helper component for editable detail rows
  const EditableDetailRow = ({ label, name, value, onChange, type = "text", inputProps = {} }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <TextField
        name={name}
        value={value || ''}
        onChange={onChange}
        type={type}
        fullWidth
        size="small"
        InputProps={inputProps}
      />
    </Box>
  );

  const formatDeliveryDate = (order) => {
    if (!order) return 'N/A';
    if (order.delivery_date === 'Immediate') {
      return `Immediate ${order.delivery_date_hours ? order.delivery_date_hours + ' hrs' : ''}`;
    } else if (order.delivery_date === 'Within') {
      return `Within ${order.delivery_date_days ? order.delivery_date_days + ' days' : ''}`;
    } else {
      return order.delivery_date || 'N/A';
    }
  };

  return (
    <Box component="section" sx={{ p: 2 , mb: 3}}>
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: "12px",
          p: { xs: 2, sm: 4 },
          margin: "auto",
          mt: 5,
        }}
      >
        {/* Title and Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
           {/* <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>Edit Quotation</Typography> */}
           <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'text.secondary' }}>
             <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{new Date(quotation.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
             <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Orderd By: {quotation.customer_order?.app_user?.company_name || ''} <br/>Buyer: {quotation.app_user?.company_name || ''}</Typography>
           </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Products Table - Show First */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Products (Enquiry ID: {quotation.custom_order_id})
            </Typography>
        
            <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: "8px" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f0f0f0" }}>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty (Tons)</TableCell>
                    <TableCell align="right">Pieces</TableCell>
                    <TableCell align="right">Cost (Editable)</TableCell>
                    <TableCell>Brand</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.product_type?.name || 'N/A'}</TableCell>
                        <TableCell align="right">{product.quantity !== null ? product.quantity : '-'}</TableCell>
                        <TableCell align="right">{product.pieces !== null ? product.pieces : '-'}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            value={product.cost || ""}
                            onChange={(e) =>
                              handleProductCostChange(product.id, e.target.value)
                            }
                            size="small"
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell>{product.brand?.name || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={products.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>
        </Grid>

        {/* Quotation Details - Show After Products */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={12}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Quotation Details
            </Typography>
            <Grid container spacing={3}>
              {/* Bending and Transport Charges side by side */}
              <Grid item xs={12} md={6}>
                {/* {quotation.customer_order?.delivery_conditions === "Bend" && ( */}
                  <EditableDetailRow
                    label="Bending Charges"
                    name="bending_charges"
                    value={quotation.bending_charges || ''}
                    onChange={handleInputChange}
                    type="text"
                    inputProps={{ 
                      inputMode: 'decimal',
                      pattern: '^\\d*\\.?\\d*$'
                    }}
                  />
                {/* )} */}
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableDetailRow
                  label="Transport Charges"
                  name="transport_charges"
                  value={quotation.transport_charges || ''}
                  onChange={handleInputChange}
                  type="text"
                  inputProps={{ 
                    inputMode: 'decimal',
                    pattern: '^\\d*\\.?\\d*$'
                  }}
                />
              </Grid>
              {/* Static Delivery Info */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Payment Terms:</Typography>
                  <Typography variant="body1">
                    {quotation?.customer_order?.payment_terms === 'Credit' 
                      ? `${quotation.customer_order.payment_terms} (${quotation.customer_order.payment_terms_description})` 
                      : quotation?.customer_order?.payment_terms || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Terms:</Typography>
                  <Typography variant="body1">
                    {quotation.customer_order?.delivery_terms === 'Delivered To' 
                      ? `${quotation.customer_order?.delivery_terms || 'N/A'}${quotation.customer_order?.delivery_address ? ' - ' + quotation.customer_order.delivery_address : ''}` 
                      : quotation.customer_order?.delivery_terms || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Condition:</Typography>
                  <Typography variant="body1">{quotation?.customer_order?.delivery_conditions || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Delivery Date:</Typography>
                  <Typography variant="body1">{formatDeliveryDate(quotation?.customer_order)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Order By:
                  </Typography>
                  <Typography variant="body1">
                    {quotation.customer_order?.app_user?.contact_person || ''} | {quotation.customer_order?.app_user?.company_name || 'N/A'}
                  </Typography>
                </Box>

               
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Totals Summary */}
        <Box sx={{ mt: 3 }}>
           <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Summary of Charges
           </Typography>
           <Grid container spacing={3}>
             <Grid item xs={12} md={12}>
                 {/* Calculate total product cost - Display side-by-side */}
               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                 <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Product Cost:</Typography>
                 <Typography variant="body1">₹ {calculateProductCost().toFixed(2)}</Typography>
              </Box>
                 {/* Bending Charges - Display side-by-side */}
               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                 <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Bending Charges:</Typography>
                 <Typography variant="body1">₹ {(parseFloat(quotation?.bending_charges) || 0).toFixed(2)}</Typography>
              </Box>
                 {/* Transport Charges - Display side-by-side */}
               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                 <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Transport Charges:</Typography>
                 <Typography variant="body1">₹ {(parseFloat(quotation?.transport_charges) || 0).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="body1" sx={{ fontWeight: 'bold' }}>GST Amount (18%):</Typography>
                   <Typography variant="body1">₹ {calculateGST().toFixed(2)}</Typography>
                </Box>
                 {/* Total Amount - Display side-by-side */}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Amount:</Typography>
                   <Typography variant="body1">₹ {calculateTotalAmount().toFixed(2)}</Typography>
                </Box>
             </Grid>
              
           </Grid>
        </Box>

         {/* Update Button */}
         <Box sx={{ mt: 4, textAlign: 'center' }}>
             <Button
               variant="contained"
               color="primary"
               onClick={handleSubmit}
               disabled={loading}
               size="large"
             >
              {loading ? <CircularProgress size={24} /> : "Update Quotation"}
             </Button>
             <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(-1)}
                sx={{ ml: 2 }}
              >
                Cancel
              </Button>
         </Box>
         
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={2000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="success">{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default EditDealerQuotation;